import { useEffect, useMemo } from 'react';
import {
  roomZoneService,
  roomTypeService,
  roomsQuery,
  roomZoneQuery,
  roomTypeQuery,
  roomsService,
} from '@lib/state';
import { addDays } from 'date-fns';
import {
  guestStaysQuery,
  guestStaysService,
  roomCountQuery,
  roomCountRequestKey,
  roomCountService,
} from 'app/state';
import { TimelineView } from './useTimeline';
import { RoomCountType } from '@lib/state/api/generated/properties';
import { useObservable } from '@lib/common';

export function useTapeChart(
  startDate: Date,
  view: TimelineView,
  countBy: RoomCountType,
  propertyId?: string | null
) {
  const guestStays = useObservable(guestStaysQuery.guestStays);
  const allRoomCounts = useObservable(roomCountQuery.roomCounts);
  const guestStaysFilters = useObservable(guestStaysQuery.guestStaysFilters);
  const roomsFilters = useObservable(roomsQuery.roomsFilters);
  const zones = useObservable(roomZoneQuery.roomZones);
  const roomTypes = useObservable(roomTypeQuery.propertyRoomTypes);
  const { roomTypeId } = useObservable(roomsQuery.roomsFilters);
  const allPropertyRooms = useObservable(roomsQuery.rooms);

  const roomTypeFilter = roomTypeId !== '' ? roomTypeId : undefined;
  const endDate = addDays(startDate, view);

  // Render only the counts produced for the current settings. The poll below clears the store in an
  // effect, which runs after the first render with a new setting; without this filter that render
  // would paint the previous setting's figures for a frame.
  const requestKey = propertyId
    ? roomCountRequestKey(propertyId, startDate, endDate, countBy, roomTypeFilter)
    : '';
  const roomCounts = useMemo(() => (allRoomCounts ?? []).filter(x => x.requestKey === requestKey), [
    allRoomCounts,
    requestKey,
  ]);

  useEffect(() => {
    roomCountService.updateIntervalFilter(startDate, addDays(startDate, view));
  }, [view, startDate]);

  useEffect(() => {
    if (propertyId) roomZoneService.getPropertyRoomZones(propertyId);
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) roomTypeService.getPropertyRoomTypes(propertyId);
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) {
      return roomsService.pollRooms(propertyId);
    }
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) {
      return roomCountService.pollRoomCounts(
        propertyId,
        startDate,
        endDate,
        countBy,
        roomTypeFilter
      );
    }
    // endDate and roomTypeFilter derive from these
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, startDate, view, roomTypeId, countBy]);

  useEffect(() => {
    if (propertyId) {
      return guestStaysService.pollGuestStays(propertyId, startDate);
    }
  }, [propertyId, startDate]);

  return {
    guestStays,
    guestStaysFilters,
    roomCounts,
    roomsFilters,
    zones,
    roomTypes,
    allPropertyRooms,
  };
}
